import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
} from "@mui/material";
import { MdNotificationsActive, MdSend } from "react-icons/md";
import { get, post } from "../../redux/ApiHelper";
import { successToast, errorToast } from "../../utils/toast.util";

type Audience =
  | "one_parent"
  | "one_teacher"
  | "all_parents"
  | "all_teachers";

type SearchUser = {
  _id: string;
  name?: string;
  phoneNumber?: number | string;
  email?: string;
};

const AUDIENCE_OPTIONS: {
  value: Audience;
  label: string;
  description: string;
}[] = [
  {
    value: "one_parent",
    label: "One Parent",
    description: "Send to a specific parent",
  },
  {
    value: "one_teacher",
    label: "One Teacher",
    description: "Send to a specific teacher",
  },
  {
    value: "all_parents",
    label: "All Parents",
    description: "Broadcast to every parent",
  },
  {
    value: "all_teachers",
    label: "All Teachers",
    description: "Broadcast to every teacher",
  },
];

const SEARCH_LIMIT = 20;
const SEARCH_DEBOUNCE_MS = 300;

const formatUserLabel = (user: SearchUser): string =>
  `${user.name || "Unknown"}${
    user.phoneNumber ? ` · ${user.phoneNumber}` : ""
  }`;

const PushNotification = () => {
  const [audience, setAudience] = useState<Audience>("one_parent");
  const [userId, setUserId] = useState("");
  const [selectedUserLabel, setSelectedUserLabel] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [users, setUsers] = useState<SearchUser[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [heading, setHeading] = useState("");
  const [message, setMessage] = useState("");
  const [redirectUrl, setRedirectUrl] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const searchRequestId = useRef(0);

  const isBlast = audience === "all_parents" || audience === "all_teachers";
  const needsUser = audience === "one_parent" || audience === "one_teacher";

  const previewToLabel = useMemo(() => {
    if (audience === "all_parents") return "All Parents";
    if (audience === "all_teachers") return "All Teachers";
    return selectedUserLabel || "—";
  }, [audience, selectedUserLabel]);

  const fetchUsers = useCallback(async (role: "parent" | "teacher", q: string) => {
    const requestId = ++searchRequestId.current;
    setIsSearching(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", String(SEARCH_LIMIT));
      if (q.trim()) params.set("q", q.trim());
      const path =
        role === "parent"
          ? `/admin/parent/search?${params.toString()}`
          : `/admin/teacher/search?${params.toString()}`;
      const res = await get(path);
      if (requestId !== searchRequestId.current) return;
      setUsers(Array.isArray(res?.data) ? res.data : []);
    } catch {
      if (requestId !== searchRequestId.current) return;
      setUsers([]);
    } finally {
      if (requestId === searchRequestId.current) {
        setIsSearching(false);
      }
    }
  }, []);

  useEffect(() => {
    setUserId("");
    setSelectedUserLabel("");
    setUserSearch("");
    setUsers([]);
  }, [audience]);

  useEffect(() => {
    if (!needsUser) return;

    const role = audience === "one_parent" ? "parent" : "teacher";
    const query = userSearch;
    const delay = query.trim() ? SEARCH_DEBOUNCE_MS : 0;
    const timer = window.setTimeout(() => {
      void fetchUsers(role, query);
    }, delay);

    return () => window.clearTimeout(timer);
  }, [userSearch, audience, needsUser, fetchUsers]);

  const canSubmit =
    heading.trim().length > 0 &&
    message.trim().length > 0 &&
    (!needsUser || !!userId);

  const handleUserSelect = (id: string) => {
    setUserId(id);
    const matched = users.find((u) => u._id === id);
    setSelectedUserLabel(matched ? formatUserLabel(matched) : "");
  };

  const submitPush = async () => {
    if (!canSubmit || isSubmitting) return;

    try {
      setIsSubmitting(true);
      const payload: Record<string, string> = {
        audience,
        heading: heading.trim(),
        message: message.trim(),
      };
      if (needsUser) payload.userId = userId;
      if (redirectUrl.trim()) payload.redirectUrl = redirectUrl.trim();
      if (imageUrl.trim()) payload.imageUrl = imageUrl.trim();

      const res = await post("/admin/push-notification", payload);
      successToast(
        res?.message
          ? `${res.message}${
              typeof res.recipientCount === "number"
                ? ` (${res.recipientCount} recipient${
                    res.recipientCount === 1 ? "" : "s"
                  })`
                : ""
            }`
          : "Notification process initiated successfully"
      );
      setHeading("");
      setMessage("");
      setRedirectUrl("");
      setImageUrl("");
      setUserId("");
      setSelectedUserLabel("");
      setUserSearch("");
      if (needsUser) {
        void fetchUsers(audience === "one_parent" ? "parent" : "teacher", "");
      }
    } catch (error: unknown) {
      const alreadyToasted =
        typeof error === "string" ||
        (error instanceof TypeError && error.message === "Failed to fetch");
      if (!alreadyToasted) {
        const msg =
          error &&
          typeof error === "object" &&
          "message" in error &&
          typeof (error as { message?: string }).message === "string"
            ? (error as { message: string }).message
            : "Failed to send notification";
        errorToast(msg);
      }
    } finally {
      setIsSubmitting(false);
      setConfirmOpen(false);
    }
  };

  const handleSendClick = () => {
    if (!canSubmit) return;
    if (isBlast) {
      setConfirmOpen(true);
      return;
    }
    void submitPush();
  };

  return (
    <div className="bg-gray-900 text-yellow-500 flex items-start justify-center p-4 flex-grow min-h-screen">
      <div className="w-full max-w-5xl grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 bg-gray-800 p-6 rounded-2xl shadow-lg border border-gray-700">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 rounded-xl bg-yellow-500/15 text-yellow-500">
              <MdNotificationsActive className="text-2xl" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-yellow-500">
                Hard Push Notification
              </h1>
              <p className="text-sm text-gray-400">
                Send a push to one user or broadcast to all parents / teachers
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
            {AUDIENCE_OPTIONS.map((opt) => {
              const selected = audience === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setAudience(opt.value)}
                  className={`text-left p-4 rounded-xl border transition ${
                    selected
                      ? "border-yellow-500 bg-yellow-500/10 shadow-[0_0_20px_rgba(234,179,8,0.12)]"
                      : "border-gray-700 bg-gray-900/40 hover:border-gray-600"
                  }`}
                >
                  <div
                    className={`font-semibold ${
                      selected ? "text-yellow-500" : "text-gray-200"
                    }`}
                  >
                    {opt.label}
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    {opt.description}
                  </div>
                </button>
              );
            })}
          </div>

          {needsUser && (
            <div className="mb-4 space-y-2">
              <label className="block text-gray-300 text-left text-sm">
                Select {audience === "one_parent" ? "Parent" : "Teacher"}
              </label>
              <input
                type="text"
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                placeholder="Search by name, phone, email..."
                className="w-full px-4 py-2 rounded-lg bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
              <select
                value={userId}
                onChange={(e) => handleUserSelect(e.target.value)}
                className="w-full px-4 py-2 rounded-lg bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              >
                <option value="">
                  {isSearching
                    ? "Searching..."
                    : `Choose a ${
                        audience === "one_parent" ? "parent" : "teacher"
                      }`}
                </option>
                {userId &&
                  !users.some((u) => u._id === userId) &&
                  selectedUserLabel && (
                    <option value={userId}>{selectedUserLabel}</option>
                  )}
                {users.map((user) => (
                  <option key={user._id} value={user._id}>
                    {formatUserLabel(user)}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 text-left">
                {`Showing up to ${SEARCH_LIMIT} matches from the server${
                  isSearching ? " · searching…" : ""
                }.`}
              </p>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-gray-300 mb-2 text-left text-sm">
                Heading <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={heading}
                onChange={(e) => setHeading(e.target.value)}
                placeholder="Notification title"
                className="w-full px-4 py-2 rounded-lg bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </div>

            <div>
              <label className="block text-gray-300 mb-2 text-left text-sm">
                Message <span className="text-red-500">*</span>
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={5}
                placeholder="Notification body"
                className="w-full px-4 py-2 rounded-lg bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500 resize-y"
              />
            </div>

            <div>
              <label className="block text-gray-300 mb-2 text-left text-sm">
                Redirect URL (optional)
              </label>
              <input
                type="text"
                value={redirectUrl}
                onChange={(e) => setRedirectUrl(e.target.value)}
                placeholder="/notice-board"
                className="w-full px-4 py-2 rounded-lg bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </div>

            {/* <div>
              <label className="block text-gray-300 mb-2 text-left text-sm">
                Image URL (optional)
              </label>
              <input
                type="text"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://..."
                className="w-full px-4 py-2 rounded-lg bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </div> */}
          </div>

          <div className="mt-6 flex justify-end">
            <button
              type="button"
              onClick={handleSendClick}
              disabled={!canSubmit || isSubmitting}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium transition ${
                !canSubmit || isSubmitting
                  ? "bg-yellow-600/50 text-gray-900 cursor-not-allowed"
                  : "bg-yellow-500 text-gray-900 hover:bg-yellow-400"
              }`}
            >
              <MdSend />
              {isSubmitting ? "Sending..." : isBlast ? "Broadcast" : "Send Push"}
            </button>
          </div>
        </div>

        <div className="lg:col-span-2 bg-gray-800 p-6 rounded-2xl shadow-lg border border-gray-700 h-fit">
          <h2 className="text-lg font-semibold text-yellow-500 mb-4">Preview</h2>
          <div className="rounded-2xl overflow-hidden border border-gray-700 bg-gradient-to-b from-gray-900 to-gray-950">
            <div className="px-4 py-3 border-b border-gray-800 flex items-center gap-2 text-xs text-gray-400">
              <span className="inline-flex h-2 w-2 rounded-full bg-yellow-500 animate-pulse" />
              Hard push ·{" "}
              {AUDIENCE_OPTIONS.find((o) => o.value === audience)?.label}
            </div>
            <div className="p-4 space-y-3">
              {imageUrl.trim() ? (
                <img
                  src={imageUrl.trim()}
                  alt="Push preview"
                  className="w-full h-32 object-cover rounded-xl border border-gray-700"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                />
              ) : null}
              <div className="text-white font-semibold text-base">
                {heading.trim() || "Notification title"}
              </div>
              <div className="text-gray-400 text-sm whitespace-pre-wrap leading-relaxed">
                {message.trim() || "Notification body will appear here."}
              </div>
              <div className="pt-2 border-t border-gray-800 text-xs text-gray-500 space-y-1">
                <div>
                  To:{" "}
                  <span className="text-yellow-500/90">{previewToLabel}</span>
                </div>
                {redirectUrl.trim() ? (
                  <div>
                    Open:{" "}
                    <span className="text-gray-300">{redirectUrl.trim()}</span>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-4 leading-relaxed">
            Broadcasts are processed asynchronously. Recipients get an in-app
            notification, FCM push, and an updated last-visit notification
            count.
          </p>
        </div>
      </div>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm broadcast</DialogTitle>
        <DialogContent>
          Send this hard push to{" "}
          <strong>
            {audience === "all_parents" ? "all parents" : "all teachers"}
          </strong>
          ? This cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={() => void submitPush()}
            color="warning"
            variant="contained"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Sending..." : "Confirm Broadcast"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default PushNotification;
